// Enums
const LockedState = {
  LOCKED: 1,
  UNLOCKED: 0
};
const RotationState = {
  ROTATION: 1,
  NONROTATION: 0
};
const ForecastValueState = {
  NA: -1,
  ZERO: 0,
  NONZERO: 1
};
const Distribution = {
  EVEN: 0,
  PROPORTIONAL: 1
};
import moment from "moment";
export default {
  getNewDayValuesForForecastWeek(newWeekValue, forecastDays, week){
    forecastDays = forecastDays.filter(f=> moment(week).isSame(moment(f.day), 'week'));
    // First we need to decide what we plan to do for the distribution.
    // There are several business rules around this.
    // Once we have decided what we are going to do, we then need to apply
    // the calculation.

    // Group the dates by their state
    let daysWithState = this.getForecastDaysWithState(forecastDays);

    // Exclude the locked total
    let lockedDays = daysWithState.filter(x => x.lockedState == LockedState.LOCKED);
    let lockedTotal = lockedDays.reduce((a, v) => a + v.contributionValue, 0);
    let unlockedNewWeekValue = newWeekValue - lockedTotal;
    if(unlockedNewWeekValue < 0){
      unlockedNewWeekValue = 0;
    }

    // Determine the distribution strategy
    let { 
      distribution, 
      distributionDaysWithState,
      distributionTotal
    } = this.getDistributionStrategyForForecastWeek(unlockedNewWeekValue, daysWithState);

    // Execute the distribution strategy
    let newValues = [];
    if(distribution == Distribution.EVEN){
      newValues = this.getValuesForTotalEvenlySplit(distributionTotal, distributionDaysWithState.length)
    } else if(distribution == Distribution.PROPORTIONAL){
      let valuesToBeDistributed = distributionDaysWithState.map(x => x.contributionValue);
      newValues = this.getValuesForTotalProportionallySplit(distributionTotal, valuesToBeDistributed)
    }

    // Join the new values with the existing unaffected values
    return daysWithState.map(x => {
      let value = x.forecastValue;

      // Lookup the new value amongst the distributions (e.g. if this day was included)
      let distributionIndex = distributionDaysWithState.findIndex(y => y.day == x.day);
      if(distributionIndex >= 0){
        value = newValues[distributionIndex];
      }

      return {
        day: x.day,
        newValue: value
      }
    });
  },
  getForecastDaysWithState(forecastDays){
    let daysWithState = [];

    for(let i = 0; i < forecastDays.length; i++){
      let forecastDay = forecastDays[i];

      let lockedState = null;
      if(forecastDay.forecastValueLocked && forecastDay.adjustedValueLocked){
        lockedState = LockedState.LOCKED;
      } else {
        lockedState = LockedState.UNLOCKED;
      }

      // Do not allow any bulk adjustments if we are under a spray condition
      if(forecastDay.isSpray || forecastDay.Withholding) {
        lockedState = LockedState.LOCKED;
      }

      let rotationState = null;
      if(forecastDay.isRotationDay){
        rotationState = RotationState.ROTATION;
      } else {
        rotationState = RotationState.NONROTATION;
      }

      let forecastValue = null;
      if(forecastDay.adjustedValue !== "" && forecastDay.forecastValueLocked){
        forecastValue = parseInt(forecastDay.adjustedValue);
      } else if(forecastDay.forecastValue !== ""){
        forecastValue = parseInt(forecastDay.forecastValue);
      } else {
        forecastValue = null;
      }

      let forecastValueState = null;
      if(forecastValue == null){
        forecastValueState = ForecastValueState.NA;
      } else if(forecastValue == 0){
        forecastValueState = ForecastValueState.ZERO;
      } else {
        forecastValueState = ForecastValueState.NONZERO;
      }

      let contributionValue = null;
      if(forecastDay.actualValue != ""){
        contributionValue = parseInt(forecastDay.actualValue);
      } else if(forecastDay.adjustedValue != "" && forecastDay.forecastValueLocked){
        contributionValue = parseInt(forecastDay.adjustedValue);
      } else if(forecastDay.forecastValue != ""){
        contributionValue = parseInt(forecastDay.forecastValue);
      } else {
        contributionValue = 0;
      }

      daysWithState.push({
        day: forecastDay.day,
        lockedState,
        rotationState,
        forecastValueState,
        forecastValue,
        contributionValue
      });
    }

    return daysWithState;
  },
  getDistributionStrategyForForecastWeek(unlockedNewWeekValue, daysWithState){
    // Get the relevant lists of days by state
    let unlockedRotationDays = daysWithState.filter(x => x.lockedState == LockedState.UNLOCKED && x.rotationState == RotationState.ROTATION);
    let unlockedRotationNonZeroNADays = daysWithState.filter(x => x.lockedState == LockedState.UNLOCKED && x.rotationState == RotationState.ROTATION && x.forecastValueState == ForecastValueState.NONZERO);
    let unlockedRotationNonZeroDays = daysWithState.filter(x => x.lockedState == LockedState.UNLOCKED && x.rotationState == RotationState.ROTATION && x.forecastValueState != ForecastValueState.ZERO);
    let unlockedNonRotationNonZeroDays = daysWithState.filter(x => x.lockedState == LockedState.UNLOCKED && x.rotationState == RotationState.NONROTATION && x.forecastValueState == ForecastValueState.NONZERO);
    let unlockedNonZeroNADays = daysWithState.filter(x => x.lockedState == LockedState.UNLOCKED && x.forecastValueState == ForecastValueState.NONZERO);
    let unlockedNonZeroDays = daysWithState.filter(x => x.lockedState == LockedState.UNLOCKED && x.forecastValueState != ForecastValueState.ZERO);
    let allUnLockedRotationDaysAreZero = daysWithState.filter(x => x.lockedState == LockedState.UNLOCKED && x.rotationState == RotationState.ROTATION).every(x => x.forecastValueState == ForecastValueState.ZERO);
    // If we have unlocked rotation days
    if (unlockedRotationDays.length > 0 && !allUnLockedRotationDaysAreZero){
      // And any of those days are non-zero
      if (unlockedRotationNonZeroNADays.length > 0){
        // Proportional split amongst all non-zero days
        console.log(`Scenario A`);
        return {
          distribution: Distribution.PROPORTIONAL,
          distributionDaysWithState: unlockedNonZeroNADays,
          distributionTotal: unlockedNewWeekValue
        };

      // Otherwise (we have no non-zero rotation days) and if we have any non-zero non-rotation days
      } else if(unlockedNonRotationNonZeroDays.length > 0){
        let excludedTotal = unlockedNonRotationNonZeroDays.reduce((a, v) => a + v.contributionValue, 0);
        let remainingNewWeekValue = unlockedNewWeekValue - excludedTotal;
        // And the remaining total is negative
        if(remainingNewWeekValue < 0){
          // Proportional split amongst all non-zero non-rotation days
          console.log(`Scenario B`);
          return {
            distribution: Distribution.PROPORTIONAL,
            distributionDaysWithState: unlockedNonRotationNonZeroDays,
            distributionTotal: unlockedNewWeekValue
          };

        // Otherwise (the remaining total is positive)
        } else {
          // Even split amongst all rotation days
          console.log(`Scenario C`);
          return {
            distribution: Distribution.EVEN,
            distributionDaysWithState: unlockedRotationNonZeroDays,
            distributionTotal: remainingNewWeekValue
          };
        }

      // Otherwise (we have no non-zero rotation days) and (we have no non-zero non-rotation days)
      } else {
        // Even split amongst all rotation days exclude zero value PE days
        console.log(`Scenario D`);
        return {
          distribution: Distribution.EVEN,
          distributionDaysWithState: unlockedRotationNonZeroDays,
          distributionTotal: unlockedNewWeekValue
        };
      }

    // Otherwise (we have no unlocked rotation days)
    } else {
      // And we have any non-zero non-rotation days
      if(unlockedNonRotationNonZeroDays.length > 0){
        // Proportional split amongst all non-zero days
        console.log(`Scenario E`);
        return {
          distribution: Distribution.PROPORTIONAL,
          distributionDaysWithState: unlockedNonZeroNADays,
          distributionTotal: unlockedNewWeekValue
        };

      } else if (unlockedRotationDays.length > 0 && allUnLockedRotationDaysAreZero) {
        // Otherwise All unlock PE days are zero and we don't have unlock non zero days, don't do anything
        console.log(`Scenario G`);
        return {
          distribution: Distribution.EVEN,
          distributionDaysWithState: unlockedNonZeroNADays,
          distributionTotal: unlockedNewWeekValue
        };
      } 
      // Otherwise (We have no non-zero non-rotation days)
      else {
        // Even split amongst all days
        console.log(`Scenario F`);
        return {
          distribution: Distribution.EVEN,
          distributionDaysWithState: unlockedNonZeroDays,
          distributionTotal: unlockedNewWeekValue
        };
      }
    }
  },
  getValuesForTotalEvenlySplit(total, numberOfValues){
    // We want to floor the average because then it is predictable how much
    // remainder we will have (it will always be >= 0 and < numberOfValues)
    // and so we can then divide it easily
    let roundEach = Math.floor(total / numberOfValues);
    let remainder = total - roundEach * numberOfValues;

    let values = [];
    for(let i = 0; i < numberOfValues; i++){
      let value = roundEach;
      if(remainder > 0){
        value++;
        remainder--;
      }
      values.push(value);
    }

    return values;
  },
  getValuesForTotalProportionallySplit(total, existingValues){
    // We want to floor the proportional value because then we can
    // account for the remainder in one direction (just ++)
    let existingTotal = existingValues.reduce((a, v) => a + v, 0);
    let newValues = existingValues.map((x, i) => {
      let value = total * x / existingTotal;
      let rounded = Math.floor(value);

      return {
        index: i,
        rounded: rounded,
        remainder: value - rounded
      };
    });

    // Assign the remainder in remainder order (e.g. the values closest
    // to rounding get assigned remainder first) - this gets us closer 
    // to the exact proportions despite rounding to whole numbers
    let totalRemainder = Math.round(newValues.reduce((a, v) => a + v.remainder, 0));
    let remainderOrder = [...newValues].sort((a, b) => {
      if(a.remainder > b.remainder){
        return -1;
      }

      if(a.remainder < b.remainder){
        return 1;
      }

      return 0;
    }).map(x => x.index);

    for(let i = 0; i < totalRemainder; i++){
      newValues[remainderOrder[i]].rounded++;
    }

    return newValues.map(x => x.rounded);
  },
  tests(){
    console.log(`Scenario A (If we have unlocked rotation days and any of those days are non-zero, then we should do a proportional split amongst all unlocked non-zero days)`);
    console.log(`%cTest A1 (2 days are non-zero non-rotation, and 4 days are non-zero rotation, and 1 day is zero non-rotation)`, `${ 
      JSON.stringify([
        {
          day: "2021-01-11",
          newValue: 56
        },
        {
          day: "2021-01-12",
          newValue: null
        },
        {
          day: "2021-01-13",
          newValue: 56
        },
        {
          day: "2021-01-14",
          newValue: 56
        },
        {
          day: "2021-01-15",
          newValue: 56
        },
        {
          day: "2021-01-16",
          newValue: 56
        },
        {
          day: "2021-01-17",
          newValue: 55
        },
      ]) == JSON.stringify(this.getNewDayValuesForForecastWeek(335, [
        {
          day: "2021-01-11",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-12",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-13",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-14",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-15",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-16",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-17",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        }
      ], "2021-01-11")) ? `color: green;` : `color: red;`
    }`);
    console.log(`Scenario B (If we have unlocked rotation days and none of those days are non-zero and we have any unlocked non-zero non-rotation days and the total of the unlocked non-zero non-rotation days is less than the new total, then we do a proportional split amongst all unlocked non-zero non-rotation days)`);
    console.log(`%cTest B1 (2 days are non-zero non-rotation, and 4 days are zero rotation, and 1 day is zero non-rotation, and the new total is less than the 2 non-zero non-rotation days)`, `${ 
      JSON.stringify([
        {
          day: "2021-01-11",
          newValue: null
        },
        {
          day: "2021-01-12",
          newValue: null
        },
        {
          day: "2021-01-13",
          newValue: null
        },
        {
          day: "2021-01-14",
          newValue: 32
        },
        {
          day: "2021-01-15",
          newValue: null
        },
        {
          day: "2021-01-16",
          newValue: 31
        },
        {
          day: "2021-01-17",
          newValue: null
        },
      ]) == JSON.stringify(this.getNewDayValuesForForecastWeek(63, [
        {
          day: "2021-01-11",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-12",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-13",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-14",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-15",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-16",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-17",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        }
      ], "2021-01-11")) ? `color: green;` : `color: red;`
    }`);
    console.log(`Scenario C (If we have unlocked rotation days and none of those days are non-zero and we have any unlocked non-zero non-rotation days and the total of the unlocked non-zero non-rotation days is more than the new total, then we do an even split amongst all unlocked rotation days)`);
    console.log(`%cTest C1 (2 days are non-zero non-rotation, and 4 days are zero rotation, and 1 day is zero non-rotation, and the new total is more than the 2 non-zero non-rotation days)`, `${ 
      JSON.stringify([
        {
          day: "2021-01-11",
          newValue: 57
        },
        {
          day: "2021-01-12",
          newValue: null
        },
        {
          day: "2021-01-13",
          newValue: 56
        },
        {
          day: "2021-01-14",
          newValue: 55
        },
        {
          day: "2021-01-15",
          newValue: 56
        },
        {
          day: "2021-01-16",
          newValue: 55
        },
        {
          day: "2021-01-17",
          newValue: 56
        },
      ]) == JSON.stringify(this.getNewDayValuesForForecastWeek(335, [
        {
          day: "2021-01-11",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-12",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-13",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-14",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-15",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-16",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-17",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        }
      ], "2021-01-11")) ? `color: green;` : `color: red;`
    }`);
    console.log(`Scenario D (If we have unlocked rotation days and none of those days are non-zero and we have no unlocked non-zero non-rotation days, then we do an even split amongst all unlocked rotation days)`);
    console.log(`%cTest D1 (4 days are zero rotation, and 3 days are zero non-rotation)`, `${ 
      JSON.stringify([
        {
          day: "2021-01-11",
          newValue: 84
        },
        {
          day: "2021-01-12",
          newValue: null
        },
        {
          day: "2021-01-13",
          newValue: 84
        },
        {
          day: "2021-01-14",
          newValue: null
        },
        {
          day: "2021-01-15",
          newValue: 84
        },
        {
          day: "2021-01-16",
          newValue: null
        },
        {
          day: "2021-01-17",
          newValue: 83
        },
      ]) == JSON.stringify(this.getNewDayValuesForForecastWeek(335, [
        {
          day: "2021-01-11",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-12",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-13",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-14",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-15",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-16",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-17",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: true,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        }
      ], "2021-01-11")) ? `color: green;` : `color: red;`
    }`);
    console.log(`Scenario E (If we have no unlocked rotation days and we have any unlocked non-zero non-rotation days, then we should a proportional split amongst all unlocked non-zero days)`);
    console.log(`%cTest E1 (all days are non-zero non-rotation)`, `${ 
      JSON.stringify([
        {
          day: "2021-01-11",
          newValue: 48
        },
        {
          day: "2021-01-12",
          newValue: 48
        },
        {
          day: "2021-01-13",
          newValue: 48
        },
        {
          day: "2021-01-14",
          newValue: 48
        },
        {
          day: "2021-01-15",
          newValue: 48
        },
        {
          day: "2021-01-16",
          newValue: 48
        },
        {
          day: "2021-01-17",
          newValue: 47
        },
      ]) == JSON.stringify(this.getNewDayValuesForForecastWeek(335, [
        {
          day: "2021-01-11",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-12",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-13",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-14",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-15",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-16",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-17",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        }
      ], "2021-01-11")) ? `color: green;` : `color: red;`
    }`);
    console.log(`%cTest E2 (all days are non-zero non-rotation but 3 days have forecast locked with no adjusted valued, only forecast value)`, `${ 
      JSON.stringify([
        {
          day: "2021-01-11",
          newValue: 48
        },
        {
          day: "2021-01-12",
          newValue: 48
        },
        {
          day: "2021-01-13",
          newValue: 48
        },
        {
          day: "2021-01-14",
          newValue: 48
        },
        {
          day: "2021-01-15",
          newValue: 48
        },
        {
          day: "2021-01-16",
          newValue: 48
        },
        {
          day: "2021-01-17",
          newValue: 47
        },
      ]) == JSON.stringify(this.getNewDayValuesForForecastWeek(335, [
        {
          day: "2021-01-11",
          forecastValueLocked: true,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-12",
          forecastValueLocked: true,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-13",
          forecastValueLocked: true,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-14",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-15",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-16",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-17",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        }
      ], "2021-01-11")) ? `color: green;` : `color: red;`
    }`);
    console.log(`%cTest E3 (3 days are locked, 4 days are non-zero non-rotation)`, `${ 
      JSON.stringify([
        {
          day: "2021-01-11",
          newValue: 55
        },
        {
          day: "2021-01-12",
          newValue: 55
        },
        {
          day: "2021-01-13",
          newValue: 55
        },
        {
          day: "2021-01-14",
          newValue: 43
        },
        {
          day: "2021-01-15",
          newValue: 43
        },
        {
          day: "2021-01-16",
          newValue: 42
        },
        {
          day: "2021-01-17",
          newValue: 42
        },
      ]) == JSON.stringify(this.getNewDayValuesForForecastWeek(335, [
        {
          day: "2021-01-11",
          forecastValueLocked: true,
          adjustedValueLocked: true,
          isRotationDay: false,
          actualValue: "55",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-12",
          forecastValueLocked: true,
          adjustedValueLocked: true,
          isRotationDay: false,
          actualValue: "55",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-13",
          forecastValueLocked: true,
          adjustedValueLocked: true,
          isRotationDay: false,
          actualValue: "55",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-14",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-15",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-16",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        },
        {
          day: "2021-01-17",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: "55"
        }
      ], "2021-01-11")) ? `color: green;` : `color: red;`
    }`);
    console.log(`Scenario F (If we have no unlocked rotation days and we have no unlocked non-zero non-rotation days, then we should an even split amongst all unlocked days)`);
    console.log(`%cTest F1 (all days are zero non-rotation)`, `${ 
      JSON.stringify([
        {
          day: "2021-01-11",
          newValue: 48
        },
        {
          day: "2021-01-12",
          newValue: 48
        },
        {
          day: "2021-01-13",
          newValue: 48
        },
        {
          day: "2021-01-14",
          newValue: 48
        },
        {
          day: "2021-01-15",
          newValue: 48
        },
        {
          day: "2021-01-16",
          newValue: 48
        },
        {
          day: "2021-01-17",
          newValue: 47
        },
      ]) == JSON.stringify(this.getNewDayValuesForForecastWeek(335, [
        {
          day: "2021-01-11",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-12",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-13",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-14",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-15",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-16",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        },
        {
          day: "2021-01-17",
          forecastValueLocked: false,
          adjustedValueLocked: false,
          isRotationDay: false,
          actualValue: "",
          adjustedValue: "",
          forecastValue: ""
        }
      ], "2021-01-11")) ? `color: green;` : `color: red;`
    }`);
  },
}